import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProperty, type Property } from '@/contexts/property-context';
import { api } from '@/lib/api';
import {
  DEFAULT_CURRENCY,
  LANGUAGE_STORAGE_KEY,
  normalizeCurrency,
  normalizeLocale,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  type SupportedCurrency,
  type SupportedLocale,
} from '@/lib/currency';
import { formatMoney, formatDate, type DateStyle } from '@/lib/format';

export { LANGUAGE_STORAGE_KEY };

/**
 * Single source of truth for switching the UI language.
 *
 * Replaces four independent implementations that had drifted apart: the
 * superadmin shell's toggle never persisted to localStorage, so the choice was
 * lost on reload. Persistence here is a localStorage cache; the server copy on
 * `users.preferred_language` is the source of truth and is synced on change.
 */
export function useLanguage() {
  const { i18n } = useTranslation();

  const current = useMemo<SupportedLanguage>(
    () => ((i18n.resolvedLanguage || i18n.language || 'en').startsWith('el') ? 'el' : 'en'),
    [i18n.resolvedLanguage, i18n.language]
  );

  const changeLanguage = (lang: SupportedLanguage) => {
    if (!SUPPORTED_LANGUAGES.includes(lang)) return;
    i18n.changeLanguage(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    if (typeof window !== 'undefined' && localStorage.getItem('koinon_token')) {
      api.patch('/auth/me', { preferred_language: lang }).catch(() => {});
    }
  };

  return { current, changeLanguage, toggle: () => changeLanguage(current === 'en' ? 'el' : 'en') };
}


/**
 * Which locale source a surface should use.
 *
 * - `property`: documents, the resident portal, notices and emails. These are
 *   official artefacts for one property, so they follow the property even when
 *   the reader's UI language differs.
 * - `chrome`: PM admin chrome (audit log, user lists, superadmin) and any view
 *   spanning several properties. There the property is not the subject, so the
 *   UI language wins.
 *
 * Defaults to `property`, which is the safe choice: it never renders a tenant a
 * document in the wrong currency.
 */
export type LocaleScope = 'property' | 'chrome';

export interface UseLocaleResult {
  currency: SupportedCurrency;
  locale: SupportedLocale;
  /** Currency symbol for the active currency (e.g. "€", "£"), for input prefixes. */
  symbol: string;
  /** Format an amount in the resolved currency/locale. */
  format: (amount: number | string | null | undefined, opts?: { showSign?: boolean }) => string;
  /** Format a date in the resolved locale using a context-appropriate style. */
  formatDate: (value: string | Date | null | undefined, style?: DateStyle) => string;
}

/**
 * The currency's symbol in the active locale, for adornments like an input's
 * currency prefix or a field label. Returns the code itself if the runtime
 * cannot produce a symbol, so the UI still shows something meaningful.
 */
function currencySymbol(currency: string, locale: string): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).formatToParts(1);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

/**
 * Resolve the active property without throwing when there is no
 * `PropertyProvider` above us.
 *
 * `useProperty()` throws by design, which is right for components that genuinely
 * require a property. `useLocale` only wants the currency/locale, and its
 * defaults are correct when no property is in scope, so a missing provider
 * falls back to EUR / el-GR instead of crashing the tree.
 */
function useOptionalProperty(): { selectedProperty?: Property | null; currentWorkspace?: any } | null {
  try {
    return useProperty();
  } catch {
    return null;
  }
}

export function useLocale(scope: LocaleScope = 'property'): UseLocaleResult {
  const { i18n } = useTranslation();
  const property = useOptionalProperty();
  const selectedProperty = property?.selectedProperty;
  const workspace = property?.currentWorkspace;

  const currency = useMemo(() => {
    if (scope === 'chrome') return DEFAULT_CURRENCY;
    const wsDefault = workspace?.default_currency;
    return normalizeCurrency(selectedProperty?.currency || wsDefault);
  }, [scope, selectedProperty?.currency, workspace?.default_currency]);

  const locale = useMemo(() => {
    if (scope === 'chrome') {
      // Chrome follows the UI language. Map 'el' -> 'el-GR' so Greek users get
      // Greek formatting even outside a property context.
      return (i18n.resolvedLanguage || i18n.language || 'en').startsWith('el')
        ? 'el-GR'
        : 'en-US';
    }
    const wsDefault = workspace?.default_locale;
    return normalizeLocale(selectedProperty?.locale || wsDefault);
  }, [scope, selectedProperty?.locale, workspace?.default_locale, i18n.resolvedLanguage, i18n.language]);

  const symbol = useMemo(() => currencySymbol(currency, locale), [currency, locale]);

  return useMemo(
    () => ({
      currency,
      locale,
      symbol,
      format: (amount: number | string | null | undefined, opts?: { showSign?: boolean }) =>
        formatMoney(amount, currency, locale, opts),
      formatDate: (value: string | Date | null | undefined, style: DateStyle = 'short') =>
        formatDate(value, locale, style),
    }),
    [currency, locale, symbol]
  );
}
